import type {
  EditorState,
  PanelDescriptor,
  PreviewPosition,
} from '#packages/stateManagement';
import * as stateManagement from '#packages/stateManagement';
import {
  WorkspaceApiKey,
  EditorRestrictionsApiKey,
  ReadonlyModeApiKey,
  EditorUIApiKey,
  FixedStageApiKey,
} from '#packages/apis';
import constants from '#packages/constants';
import { modes } from '#packages/util';
import type {
  MapDispatchToProps,
  MapStateToProps,
  ThunkAction,
  Dispatch,
} from 'types/redux';
import type { ContributedComponent } from '#packages/apilib';
import type { CompData, CompRef } from 'types/documentServices';
import type { ViewerMouseCoordinates } from '#packages/util';
import {
  editorAPISel,
  editorStateMouseOpsSel,
} from '../selectors/rootSelectors';
import { isDuringMouseActionSel } from '../selectors/mouseSelectors';
import { createSelector } from '../../selectors/selector';
import type { StageContainerOwnProps } from './stageContainer';
import type { MouseEvent, MouseEventHandler } from 'react';
import { type MouseMoveAction } from '#packages/rEditor';
import { AI_TEXT_CREATOR_ENTRY_POINT_ORIGIN } from './stageContainerConstants';

const { isInInteractionMode } = stateManagement.interactions.selectors;
const { isTranslating } = stateManagement.multilingual.selectors;
const { isPerformingMouseMoveAction } = stateManagement.mouseActions.selectors;
const { getSelectedCompsRefs, getFocusedContainer } =
  stateManagement.selection.selectors;
const { isOpen: isPinModeOpen } = stateManagement.pinMode.selectors;
const { getPreviewPosition, translateToViewerCoordinates } =
  stateManagement.domMeasurements.selectors;
const { selectApplyModeFromClipboardSuggestion } =
  stateManagement.applyModeFromClipboard.selectors;
const { closeOpenedPanels } = stateManagement.panels.actions;
const { openContextMenu } = stateManagement.contextMenu.actions;

export interface StageContainerStateProps {
  StageComponents: ContributedComponent<{}>[];
  siteScale: EditorState['siteScale'];
  editorIsInit: EditorState['editorIsInit'];
  imageCrop: EditorState['imageCrop'];
  rulersEnabled: boolean;
  pageSections: EditorState['pageSections'];
  halfOpacityTools: EditorState['halfOpacityTools'];
  previewPosition: PreviewPosition;
  performingMouseMoveAction: boolean;
  registeredMouseMoveAction: MouseMoveAction | null;
  isPreviewReady: boolean;
  isMobileEditor: boolean;
  isInteractionMode: boolean;
  isTranslationMode: boolean;
  isPinMode: boolean;
  isPopUpMode: boolean;
  isMobileOptimizedSite: boolean;
  isInZoomMode: boolean;
  isStageZoomMode: boolean;
  isZoomModeTransitionActive: boolean;
  isRightClickMenuDisabled: boolean;
  isReorganizeHidden: boolean;
  selectedComponents: CompRef[];
  focusedContainer: CompRef;
  openPanels: PanelDescriptor[];
  isStageInteractive: boolean;
  isScalableMouseCatcherVisible: boolean;
  isStyleOnlyMode: boolean;
  isActionsHoveredAndHideSelectionEnabled: boolean;
  isTruncated: boolean;
  isStageFixedInDesktop: boolean;
}

export const mapStateToProps: MapStateToProps<
  StageContainerStateProps,
  StageContainerOwnProps
> = (stateMapperArgs) => {
  const { state: editorState, editorAPI, host } = stateMapperArgs;

  const {
    config,
    siteScale,
    editorIsInit,
    hideTools,
    imageCrop,
    tabIndicationState,
    constraintArea,
    viewTools: { rulersEnabled },
    pageSections,
    halfOpacityTools,
  } = editorState;

  const StageComponents = host.getAPI(WorkspaceApiKey).getStageComponents();
  const isReadonlyModeEnabled = host
    .getAPI(ReadonlyModeApiKey)
    .isReadonlyModeEnabled();

  const previewPosition = getPreviewPosition(editorState);
  const performingMouseMoveAction = isPerformingMouseMoveAction(editorState);
  const selectedComponents = getSelectedCompsRefs(editorState);
  const applyModeFromClipboardSuggestion =
    selectApplyModeFromClipboardSuggestion(editorState);
  const isInteractionMode = isInInteractionMode(editorState);
  const isTranslationMode = isTranslating(editorState);
  const isPinMode = isPinModeOpen(editorState);
  const focusedContainer = getFocusedContainer(editorState);

  const isPopUpMode = editorAPI.isPopUpMode();
  const isMobileOptimizedSite = editorAPI.isMobileOptimizedSite();
  const isInZoomMode = editorAPI.zoomMode.isInZoomMode();
  const isStageZoomMode = editorAPI.zoomMode.isStageZoomMode();
  const isPreviewReady = editorAPI.isPreviewReady();
  const isMobileEditor = editorAPI.isMobileEditor();
  const isRightClickMenuDisabled =
    isReadonlyModeEnabled ||
    (isInZoomMode && !editorAPI.zoomMode.enabledRightClickMenu());

  const registeredMouseMoveAction = performingMouseMoveAction
    ? editorAPI.mouseActions.getRegisteredMouseMoveAction()
    : null;

  const isReorganizeHidden = hideTools || isPopUpMode;
  const openPanels = editorAPI.getOpenPanels();

  const editorRestrictionsApi = editorAPI.host.getAPI(EditorRestrictionsApiKey);
  const isStageInteractive = editorRestrictionsApi.allowed(
    'rEditor_stage-container.interactive',
  );
  const isScalableMouseCatcherVisible = editorRestrictionsApi.allowed(
    'rEditor_mouse-catcher_scalable.visible',
  );

  const isStyleOnlyMode = modes.isStyleOnlyModeEnabled(
    editorAPI,
    selectedComponents,
  );

  const isActionsHoveredAndHideSelectionEnabled =
    stateManagement.selection.selectors.shouldHideSelectionWhenStageActionsHovered(
      editorAPI,
    );

  return {
    StageComponents,
    siteScale,
    editorIsInit,
    isPreviewReady,
    isMobileEditor,
    performingMouseMoveAction,
    registeredMouseMoveAction,
    imageCrop,
    tabIndicationState,
    constraintArea,
    rulersEnabled,
    config,
    pageSections,
    halfOpacityTools,
    previewPosition,
    isMobileOptimizedSite,
    isInZoomMode,
    isStageZoomMode,
    selectedComponents,
    applyModeFromClipboardSuggestion,
    isRightClickMenuDisabled,
    focusedContainer,
    isInteractionMode,
    isTranslationMode,
    isZoomModeTransitionActive: editorAPI.zoomMode.isZoomModeTransitionActive(),
    isPinMode,
    isPopUpMode,
    isReorganizeHidden,
    openPanels,
    isStageInteractive,
    isScalableMouseCatcherVisible,
    isStyleOnlyMode,
    isActionsHoveredAndHideSelectionEnabled,
    isTruncated: editorAPI.host
      .getAPI(EditorUIApiKey)
      .stage.shouldCutEditingArea(),
    isStageFixedInDesktop: editorAPI.host
      .getAPI(FixedStageApiKey)
      .isStageFixedInDesktop(),
  };
};

const getEditorAPI: ThunkAction = (
  dispatch,
  getState,
  { editorAPI, host },
) => ({
  editorAPI,
  state: getState(),
  host,
});

export interface StageContainerDispatchProps {
  getEditingAreaLayout: () => any;
  translateToViewerCoordinates: (event: MouseEvent) => ViewerMouseCoordinates;
  closeOpenedPanels: () => void;
  openContextMenu: (config: any) => void;
  selectComponentByClick: MouseEventHandler;
  getSelectedComponents: () => CompRef[];
  getComponentData: (compRef: CompRef) => CompData;
  getIsSpotlightStageContainer: (focusedContainer: CompRef) => boolean;
  getIsVerticalTextComp: (compRef: CompRef) => boolean;
  fireMouseLeave: MouseEventHandler;
  exitZoomMode: (biParams: { origin: string }) => void;
  openAiTextGeneratorPanel: () => void;
}

export const mapDispatchToProps: MapDispatchToProps<
  StageContainerDispatchProps,
  StageContainerOwnProps
> = (dispatch: Dispatch) => {
  const { editorAPI, host } = dispatch(getEditorAPI);

  return {
    getEditingAreaLayout: () => editorAPI.ui.stage.getEditingAreaLayout(),
    translateToViewerCoordinates: (event: MouseEvent) =>
      translateToViewerCoordinates(editorAPI, event),
    closeOpenedPanels: () => dispatch(closeOpenedPanels()),
    openContextMenu: (config: AnyFixMe) => dispatch(openContextMenu(config)),
    selectComponentByClick: (event: MouseEvent) =>
      editorAPI.selection.selectComponentByClick(event),
    // need this because selectComponentByClick doesn't return selected component which we need in the same cycle
    getSelectedComponents: () =>
      getSelectedCompsRefs(editorAPI.store.getState()),
    getComponentData: (compRef: CompRef) =>
      editorAPI.components.data.get(compRef),
    getIsVerticalTextComp: (compRef: CompRef) =>
      editorAPI.components.properties.get(compRef)?.verticalText,
    getIsSpotlightStageContainer: (focusedContainer: CompRef) =>
      editorAPI.components.is.spotlightStageContainer(focusedContainer),
    fireMouseLeave: () => {
      host.getAPI(WorkspaceApiKey).hooks.stageMouseLeave.fire();
    },
    exitZoomMode: (biParams: any) => {
      const isStageZoomMode = editorAPI.zoomMode.isStageZoomMode();
      const isZoomModeAnimation =
        editorAPI.zoomMode.isZoomModeTransitionActive();

      if (isStageZoomMode && !isZoomModeAnimation) {
        editorAPI.zoomMode.exitZoomMode({ biParams });
      }
    },
    openAiTextGeneratorPanel: () => {
      editorAPI.openComponentPanel(constants.componentPanels.aiTextGenerator, {
        origin: AI_TEXT_CREATOR_ENTRY_POINT_ORIGIN,
      });
    },
  };
};

const isPreviewReadySel = createSelector([editorAPISel], (editorAPI) =>
  editorAPI.isPreviewReady(),
);

const stageHeightSel = createSelector(
  [
    isPreviewReadySel,
    editorStateMouseOpsSel,
    editorAPISel,
    isDuringMouseActionSel,
  ],
  (isPreviewReady, editorState, editorAPI, isDuringMouseAction) => {
    if (!isPreviewReady) {
      return '100%';
    }

    const siteHeight = isDuringMouseAction
      ? editorState.config.previewHeight
      : editorAPI.dsRead.site.getHeight();

    return siteHeight * editorState.siteScale;
  },
);

export const siteCoverStyleSel = createSelector([stageHeightSel], (height) => ({
  height,
}));
